import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Download, Mail, Mic, Clock } from "lucide-react";
import { toast } from "sonner";
import { SummaryTab } from "@/components/meeting/SummaryTab";
import { TranscriptTab } from "@/components/meeting/TranscriptTab";
import { ChatTab } from "@/components/meeting/ChatTab";

const STATUS_BADGES = {
  completed: { label: "Terminée", cls: "bg-[#F5EFD8] text-[#3A2E1F] border-[#E5D9B8] border rounded-sm" },
  in_progress: { label: "En cours", cls: "bg-[#FFE4E6] text-[#9F1239] border-[#FECDD3] border rounded-sm" },
  planned: { label: "Planifiée", cls: "bg-[#FDF1E4] text-[#9A5B13] border-[#FDE2C0] border rounded-sm" },
};

export default function MeetingDetailPage() {
  const { id } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summarizing, setSummarizing] = useState(false);

  const load = () => {
    api
      .get(`/meetings/${id}`)
      .then(({ data }) => setMeeting(data))
      .catch((err) => console.error("Failed to load meeting:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onRegenerateSummary = async () => {
    setSummarizing(true);
    try {
      await api.post(`/meetings/${id}/summarize`);
      toast.success("Résumé IA généré");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setSummarizing(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("afrimeet_token");
      const res = await fetch(`${API}/meetings/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AfriMeet_${meeting.title.replace(/[^a-z0-9]/gi, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Erreur de téléchargement");
    }
  };

  const distribute = async () => {
    const recipients = meeting.participants || [];
    if (recipients.length === 0) {
      toast.error("Aucun participant. Ajoutez des emails de participants à la réunion.");
      return;
    }
    try {
      const { data } = await api.post(`/meetings/${id}/distribute`, { recipients, message: "" });
      if (data.status === "sent") {
        toast.success(`Email envoyé à ${recipients.length} participant(s) avec le PDF en pièce jointe.`);
      } else if (data.status === "queued_mock") {
        toast.warning("Distribution enregistrée mais email non envoyé (clé Resend manquante).");
      } else {
        toast.info("Distribution traitée.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    }
  };

  const toggleAction = async (actionId, newCompleted) => {
    const patch = (completed) => (m) => ({
      ...m,
      summary: {
        ...m.summary,
        action_items: (m.summary?.action_items || []).map((a) =>
          a.id === actionId ? { ...a, completed } : a
        ),
      },
    });
    setMeeting(patch(newCompleted)); // optimistic
    try {
      await api.patch(`/meetings/${id}/actions/${actionId}`, { completed: newCompleted });
      toast.success(newCompleted ? "Action validée ✓" : "Action remise à faire");
    } catch (e) {
      setMeeting(patch(!newCompleted)); // revert
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (loading || !meeting) return <div className="p-8 text-af-muted">Chargement…</div>;

  const { summary, transcript, status } = meeting;
  const badge = STATUS_BADGES[status] || STATUS_BADGES.planned;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <Link to="/app/meetings" className="inline-flex items-center gap-2 text-sm text-af-secondary hover:text-af mb-6" data-testid="back-link">
        <ArrowLeft className="h-4 w-4" /> Toutes les réunions
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge className={badge.cls}>{badge.label}</Badge>
            <span className="text-xs text-af-muted flex items-center gap-1">
              <Clock className="h-3 w-3" /> {Math.round((meeting.duration_seconds || 0) / 60)} min
            </span>
          </div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">{meeting.title}</h1>
          {meeting.description && <p className="text-af-secondary mt-2 leading-relaxed max-w-2xl">{meeting.description}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {status !== "completed" && (
            <Link to={`/app/meetings/${id}/room`} data-testid="enter-room-btn">
              <Button className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md">
                <Mic className="h-4 w-4 mr-2" /> Entrer dans la salle
              </Button>
            </Link>
          )}
          {summary && (
            <>
              <Button onClick={downloadPdf} variant="outline" className="border-af rounded-md" data-testid="download-pdf-btn">
                <Download className="h-4 w-4 mr-2" /> PDF
              </Button>
              <Button onClick={distribute} variant="outline" className="border-af rounded-md" data-testid="distribute-btn">
                <Mail className="h-4 w-4 mr-2" /> Distribuer
              </Button>
            </>
          )}
        </div>
      </div>

      {!transcript && status !== "completed" && (
        <Card className="p-8 bg-af-card border-af rounded-md text-center mb-6" data-testid="no-transcript-card">
          <Mic className="h-8 w-8 mx-auto text-af-muted mb-3" />
          <p className="text-af-secondary">Pas encore d'enregistrement. Entrez dans la salle pour démarrer.</p>
        </Card>
      )}

      {transcript && (
        <Tabs defaultValue="summary" className="space-y-6">
          <TabsList className="bg-af-subtle border border-af rounded-md p-1">
            <TabsTrigger value="summary" data-testid="tab-summary" className="data-[state=active]:bg-white data-[state=active]:text-terracotta rounded-sm">
              Résumé IA
            </TabsTrigger>
            <TabsTrigger value="transcript" data-testid="tab-transcript" className="data-[state=active]:bg-white data-[state=active]:text-terracotta rounded-sm">
              Transcription
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat" className="data-[state=active]:bg-white data-[state=active]:text-terracotta rounded-sm">
              Assistant IA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-4">
            <SummaryTab
              summary={summary}
              summarizing={summarizing}
              onRegenerate={onRegenerateSummary}
              onToggleAction={toggleAction}
            />
          </TabsContent>

          <TabsContent value="transcript">
            <TranscriptTab transcript={transcript} />
          </TabsContent>

          <TabsContent value="chat">
            <ChatTab meetingId={id} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
